var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import stylesBreakpoint from 'Styles/breakpoints';
import { setChildMap } from '../../utils/utils';
import { createContentCardMapEntries } from '../Card/Card.utils';
/**
 * The initial key in the Map represents how many cards the section will have across on large screens
 * The two numbers in the array represent how many cards across will be on medium and small screens respectively
 *
 * @example
 * cardLayout.set(6, [4, 3]) represents ACROSS_6 on desktop, 4 on medium and 3 on small
 */
var cardLayoutDefaults = new Map([
    ['ACROSS_6', ['ACROSS_4', 'ACROSS_3']],
    ['ACROSS_5', ['ACROSS_4', 'ACROSS_3']],
    ['ACROSS_4', ['ACROSS_4', 'ACROSS_2']],
    ['ACROSS_3', ['ACROSS_3', 'ACROSS_1']],
    ['ACROSS_2', ['ACROSS_2', 'ACROSS_1']],
    ['ACROSS_1', ['ACROSS_2', 'ACROSS_1']]
]);
export var getDefaultCardLayout = function (largeScreenAcross) {
    return cardLayoutDefaults.get(largeScreenAcross) || ['ACROSS_4', 'ACROSS_3'];
};
var mapLayoutOptionsToNumber = new Map([
    ['ACROSS_1', 1],
    ['ACROSS_2', 2],
    ['ACROSS_3', 3],
    ['ACROSS_4', 4],
    ['ACROSS_5', 5],
    ['ACROSS_6', 6]
]);
/**
 * With a given amount of cards across in a card section, return its equivalent number
 */
export var convertLayoutOptionToNumber = function (option) {
    return mapLayoutOptionsToNumber.get(option);
};
var cardWidthToTextSize = new Map();
cardWidthToTextSize.set('SMALL', setChildMap('ACROSS_1', 'MEGA')
    .set('ACROSS_2', 'MINI')
    .set('ACROSS_3', 'MINI'));
cardWidthToTextSize.set('MEDIUM', setChildMap('ACROSS_2', 'MEGA')
    .set('ACROSS_1', 'MEGA')
    .set('ACROSS_3', 'REGULAR')
    .set('ACROSS_4', 'MINI'));
cardWidthToTextSize.set('LARGE', setChildMap('ACROSS_2', 'MEGA')
    .set('ACROSS_1', 'MEGA')
    .set('ACROSS_3', 'MEGA')
    .set('ACROSS_4', 'REGULAR')
    .set('ACROSS_5', 'MINI')
    .set('ACROSS_6', 'MINI'));
/**
 * Given a screen size and a card section width, return a text size for a section breakpoint
 */
export var getCardTextSizeFromCardWidth = function (_a) {
    var _b;
    var screenSize = _a.screenSize, width = _a.width;
    return (_b = cardWidthToTextSize.get(screenSize)) === null || _b === void 0 ? void 0 : _b.get(width);
};
/**
 * Calculates the visible slides count.
 * If there is only one slide visible (Across 1) and it's Small or Medium breakpoints, two slides should be shown.
 * @param $visibleSlidesCount - The initial count of visible slides extracted from the layout.
 * @returns {number} - The adjusted count of visible slides.
 */
export var getVisibleSlidesCount = function ($visibleSlidesCount, $large) {
    if ($visibleSlidesCount === 1 && !$large)
        return 2;
    return $visibleSlidesCount;
};
/**
 * Determines the number of slides to show for different screen sizes.
 * If there is only one slide, two slides should be shown instead.
 * @param screenAcross - The number of slides configured for the screen size, can be undefined.
 * @param defaultAcross - The default number of slides for the screen size, as a string.
 * @returns {number} - The calculated number of slides.
 */
export var numSlidesCalc = function (screenAcross, defaultAcross) { return (screenAcross === 1 ? 2 : screenAcross || defaultAcross); };
/**
 * Generates CSS grid and flex styles based on the provided parameters.
 *
 * @param {number} [screenAcross] - Number of columns to span across the screen.
 * @param {number} [totalGridColumns] - Total number of grid columns. Defaults to `defaultGridColumns` if not provided.
 * @param {number} [padding=16] - Padding between grid items in pixels. Defaults to 16.
 * @param {number} [defaultGridColumns=12] - Default total number of grid columns if `totalGridColumns` is not provided. Defaults to 12.
 * @returns {ReturnType<typeof css> | false} - A CSS fragment for styled-components or false if `screenAcross` is not provided.
 */
export var getGridStyles = function (screenAcross, totalGridColumns, padding, defaultGridColumns) {
    if (padding === void 0) { padding = 16; }
    if (defaultGridColumns === void 0) { defaultGridColumns = 12; }
    return screenAcross &&
        "\n    grid-column: span\n      ".concat(totalGridColumns
            ? totalGridColumns / screenAcross
            : defaultGridColumns / screenAcross, ";\n    flex: 0 0 calc(").concat((1 / screenAcross) * 100, "% - ").concat(padding, "px);\n  ");
};
export var contentCardimageResolutionsByLayout = new Map(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], createContentCardMapEntries(['ACROSS_1'], 'LARGEST'), true), createContentCardMapEntries(['ACROSS_2'], 'LARGE'), true), createContentCardMapEntries(['ACROSS_3', 'ACROSS_4'], 'MEDIUM'), true), createContentCardMapEntries(['ACROSS_5', 'ACROSS_6'], 'SMALL'), true));
export var contentCardimageResolutionsByGalleryLayout = new Map(__spreadArray(__spreadArray([], createContentCardMapEntries(['ACROSS_1'], 'LARGEST'), true), createContentCardMapEntries(['ACROSS_2', 'ACROSS_3', 'ACROSS_4', 'ACROSS_5'], 'LARGE'), true));
export var contentCardimageResolutionsByProductGrid = new Map(__spreadArray(__spreadArray([], createContentCardMapEntries(['ACROSS_1', 'ACROSS_2'], 'LARGE'), true), createContentCardMapEntries(['ACROSS_3', 'ACROSS_4'], 'MEDIUM'), true));
export var setGridTemplateColumnsForWrap = function (smallScreenAcross, mediumScreenAcross, largeScreenAcross, styleQueryMode) {
    if (styleQueryMode === void 0) { styleQueryMode = 'screen'; }
    return "\n    grid-template-columns: repeat(".concat(smallScreenAcross, ", minmax(0px, 1fr));\n    ").concat(stylesBreakpoint('medium', { mode: styleQueryMode }), " {\n      grid-template-columns: repeat(\n        ").concat(mediumScreenAcross, ",\n        minmax(0px, 1fr)\n      );\n    }\n    ").concat(stylesBreakpoint('large', { mode: styleQueryMode }), " {\n      grid-template-columns: repeat(").concat(largeScreenAcross, ", minmax(0px, 1fr));\n    }\n  ");
};
export var isSmallContainer = function (containerQueryClassName) {
    return containerQueryClassName.includes('less-than-medium');
};
export var needsSelectCarousel = function (_a) {
    var styleQueryMode = _a.styleQueryMode, containerQueryClassName = _a.containerQueryClassName, hasOverflow = _a.hasOverflow;
    return styleQueryMode === 'container' &&
        isSmallContainer(containerQueryClassName) &&
        hasOverflow;
};
export default {};
